import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";

interface AutoPreviewContextType {
  activePreviewId: string | null;
  setActivePreviewId: (id: string | null) => void;
  registerCard: (id: string, el: HTMLElement) => void;
  unregisterCard: (id: string) => void;
  onPreviewEnded: (id: string) => void;
  removeBrokenId: (id: string) => void;
}

const emptyFn = () => { };
const defaultContextValue: AutoPreviewContextType = {
  activePreviewId: null,
  setActivePreviewId: emptyFn,
  registerCard: emptyFn,
  unregisterCard: emptyFn,
  onPreviewEnded: emptyFn,
  removeBrokenId: emptyFn,
};

const AutoPreviewContext = createContext<AutoPreviewContextType>(defaultContextValue);
const START_DELAY_MS = 2500;

export const AutoPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const sequenceIndexRef = useRef<number>(0);
  const allElementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const id = (entry.target as HTMLElement).dataset.gameId;
        if (!id) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          setVisibleIds(prev => prev.includes(id) ? prev : [...prev, id]);
        } else {
          setVisibleIds(prev => prev.filter(vId => vId !== id));
          return;
        }
      });
    }, {
      threshold: [0.5]
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const clearGlobalTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const startSequence = useCallback(() => {
    clearGlobalTimers();
    timerRef.current = setTimeout(() => {
      if (visibleIds.length > 0) {
        const idx = sequenceIndexRef.current % visibleIds.length;
        setActivePreviewId(visibleIds[idx]);
      }
    }, START_DELAY_MS);
  }, [visibleIds]);

  useEffect(() => {
    if (activePreviewId && !visibleIds.includes(activePreviewId || "")) {
      sequenceIndexRef.current = 0;
      setActivePreviewId(null);
      clearGlobalTimers();
      return;
    }
    if (!activePreviewId && visibleIds.length > 0) {
      startSequence();
    }
  }, [activePreviewId, visibleIds, startSequence]);

  const goNext = useCallback(() => {
    setVisibleIds((currentVisible) => {
      if (currentVisible.length === 0) return currentVisible;

      sequenceIndexRef.current = (sequenceIndexRef.current + 1) % currentVisible.length;
      setActivePreviewId(currentVisible[sequenceIndexRef.current]);
      return currentVisible;
    });
  }, []);

  useEffect(() => {
    if (visibleIds.length <= 1) return;
    setVisibleIds((prev) => {
      const rects = new Map();
      prev.forEach(id => {
        const el = allElementsRef.current.get(id);
        if (el) rects.set(id, el.getBoundingClientRect());
      });

      const sorted = [...prev].sort((a, b) => {
        const rectA = rects.get(a);
        const rectB = rects.get(b);
        if (!rectA || !rectB) return 0;

        const topDiff = rectA.top - rectB.top;
        if (Math.abs(topDiff) > 15) return topDiff;
        return rectA.left - rectB.left;
      });
      return sorted.join(',') === prev.join(',') ? prev : sorted;
    });
  }, [visibleIds.length]);


  const brokenIdsRef = useRef<Set<string>>(new Set());

  const removeBrokenId = useCallback((id: string) => {
    brokenIdsRef.current.add(id);
    setVisibleIds((prev) => {
      const currentIndex = prev.indexOf(id);
      if (currentIndex === -1) return prev;
      const nextVisibleIds = prev.filter((vId) => vId !== id);
      setActivePreviewId((currentActive) => {
        if (currentActive !== id) return currentActive;
        if (nextVisibleIds.length === 0) return null;
        return nextVisibleIds[currentIndex % nextVisibleIds.length];
      });
      return nextVisibleIds;
    });
  }, [])

  const registerCard = useCallback((id: string, el: HTMLElement) => {
    if (brokenIdsRef.current.has(id)) return;
    if (!id || !el) return;
    allElementsRef.current.set(id, el);
    el.dataset.gameId = id;
    observerRef.current?.observe(el);
  }, []);

  const unregisterCard = useCallback((id: string) => {
    if (!id) return;
    setVisibleIds((prev) => {
      if (!prev.includes(id)) return prev;
      return prev.filter((vId) => vId !== id);
    });
    setActivePreviewId((current) => (current === id ? null : current));
  }, []);

  return (
    <AutoPreviewContext.Provider value={{ activePreviewId, setActivePreviewId, registerCard, unregisterCard, onPreviewEnded: goNext, removeBrokenId }}>
      {children}
    </AutoPreviewContext.Provider>
  );
};

export const useAutoPreview = () => {
  return useContext(AutoPreviewContext);
};